var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Schema } from '../schema.js';
import { SourcedValue } from '../option.js';
import { productTypeGuard, RecordKind } from '../record.js';
import { EnumType, ListType, ObjectType, PrimitiveType, Property, SystemType, TypeKind, UnionType } from '../types.js';
import { createExpressionConstraint } from '../constraint.js';
import { PropertyPathWalker } from '../internal/PropertyPathWalker.js';
import deepEqual from 'deep-equal';
import { PrototypeTraverser, SimpleVisitor, ValidationVisitor } from '../internal/PrototypeVisitor.js';
import { engineUtils } from '../services/EngineUtils.js';
import { OclConstraintRegistry } from '../services/OclConstraintRegistry.js';
import { ExternalFieldRegistry } from '../services/ExternalFieldRegistry.js';
import { PrimitiveValidator } from '../validators/PrimitiveValidator.js';
import { ExternalParser } from '../parsers/ExternalParser.js';
import { PropertyPathParser } from '../parsers/PropertyPathParser.js';
import { PrimitiveParser } from '../parsers/PrimitiveParser.js';
import { FilterRegistry } from '../services/FilterRegistry.js';
import { CompatibilityTablesDescriptorParser } from './CompatibilityTablesDescriptorParser.js';
import { FlatProductTransformer } from '../services/transformers/ProductTransformer.js';
import { ResolutionSchemaParser } from '../parsers/ResolutionSchemaParser.js';
import { SchemaError } from '../errors/SchemaError.js';
import { NullYamlTransformer } from './YamlTransformer.js';
import { OptionsRegistry } from './OptionsRegistry.js';
import { DataFile } from './visitors/DataFile.js';
import { CompositeDataFileVisitor } from './visitors/CompositeDataFileVisitor.js';
import { TransformingDataFileVisitor } from './visitors/TransformingDataFileVisitor.js';
import { ParsingDataFileVisitor } from './visitors/ParsingDataFileVisitor.js';
import { PostprocessingProductVisitor } from './visitors/PostprocessingProductVisitor.js';
function isPropertyOptionsEqual(opts, opts2) {
    return deepEqual(opts, opts2);
}
function isPropertiesIncompatible(property, componentProperty, ignoreDefault = false) {
    let aOpts;
    let bOpts;
    if (ignoreDefault) {
        const _a = property.opts, { default: componentValue } = _a, propOpts = __rest(_a, ["default"]);
        aOpts = propOpts;
        const _b = componentProperty.opts, { default: objectValue } = _b, componentOpts = __rest(_b, ["default"]);
        bOpts = componentOpts;
    }
    else {
        aOpts = property.opts;
        bOpts = componentProperty.opts;
    }
    return property.name === componentProperty.name &&
        (property.type !== componentProperty.type ||
            !isPropertyOptionsEqual(aOpts, bOpts));
}
function overrideProperties(typeRef, component, object) {
    const map = new Map();
    component.forEach(property => map.set(property.name, property));
    object.forEach(objectProp => {
        if (map.has(objectProp.name)) {
            const componentProp = map.get(objectProp.name);
            if (isPropertiesIncompatible(componentProp, objectProp, true)) {
                throw new Error(`Wrong type definition for ${typeRef}. Cannot override property ${objectProp.name}`);
            }
        }
        map.set(objectProp.name, objectProp);
    });
    return Array.from(map.values());
}
function castProduct(product) {
    if (!product || product.kind !== 'Product') {
        throw new Error('Invalid product');
    }
    return product;
}
export class AsyncJsDatasetService {
    constructor() {
        this.optionsRegistry = new OptionsRegistry();
        this.products = {};
        this.compatibilityTables = new Map();
    }
    static createService(schema, data = [], transformer = new NullYamlTransformer()) {
        return __awaiter(this, void 0, void 0, function* () {
            const datasetService = new AsyncJsDatasetService();
            yield datasetService.init(schema, data, [], transformer);
            return datasetService;
        });
    }
    init(loadedSchema, dataFiles = [], plugins = [], transformer = new NullYamlTransformer()) {
        return __awaiter(this, void 0, void 0, function* () {
            if (loadedSchema.kind !== RecordKind.Schema) {
                throw new Error('Illegal schema file');
            }
            this.schema = new Schema();
            this.typesMap = new Map();
            this.constraintRegistry = new OclConstraintRegistry(this.schema);
            this.filterRegistry = new FilterRegistry(this.schema);
            this.externalFieldRegistry = new ExternalFieldRegistry(this.schema);
            this.primitiveValidator = new PrimitiveValidator(this.schema);
            this.primitiveParser = new PrimitiveParser(this.primitiveValidator);
            this.propertyPathValidator = new PropertyPathParser(this.schema);
            this.externalParser = new ExternalParser(this.primitiveValidator, this.propertyPathValidator);
            const languages = loadedSchema.languages || [];
            this.schema.addLanguageSupport(...languages);
            for (let typesKey in loadedSchema.types) {
                let type = loadedSchema.types[typesKey];
                if (this.typesMap.has(type.name)) {
                    throw new Error(`Wrong schema: type ${type.name} is duplicated`);
                }
                this.typesMap.set(type.name, type);
            }
            for (let type of this.typesMap.values()) {
                this.registerType(type);
            }
            for (let type of Object.values(this.schema.types)) {
                if (type.kind === TypeKind.Object) {
                    this.parseExternals(type);
                }
            }
            const visitor = new CompositeDataFileVisitor(new TransformingDataFileVisitor(transformer), new ParsingDataFileVisitor(this.optionsRegistry, this.products, this.constraintRegistry, this.filterRegistry, this.externalParser, this.externalFieldRegistry, this.schema, this.compatibilityTables), new PostprocessingProductVisitor(transformer));
            for (let data of dataFiles) {
                data.forEach(doc => {
                    const dataFile = new DataFile(doc);
                    dataFile.accept(visitor);
                });
            }
            const pluginRegistry = {};
            for (let plugin of plugins) {
                if (typeof plugin === 'string') {
                    eval(`(function (window) {
          ${plugin}
        })`)(pluginRegistry);
                }
                else {
                    this.schema.registerPlugin(plugin);
                    console.log('PluginBase detected!');
                }
            }
            for (let value of Object.values(pluginRegistry)) {
                const plugin = new value.default();
                this.schema.registerPlugin(plugin);
            }
            this.parseResolutionSchema(loadedSchema);
            this.schema.validate();
            this.validateProducts();
        });
    }
    splitProducts() {
        const products = [];
        const productGroups = [];
        for (const record of Object.values(this.products)) {
            if (productTypeGuard(record)) {
                products.push(record);
            }
            else {
                productGroups.push(record);
            }
        }
        return [productGroups, products];
    }
    getExternalFieldRegistry(typeRef) {
        return this.externalFieldRegistry;
    }
    findOneProductInProductGroupByExample(productGroupName, example) {
        let products = engineUtils.flattenProducts(productGroupName);
        for (let product of products) {
            let match = true;
            for (let key in example) {
                if (example[key] && product.model[key] !== example[key]) {
                    match = false;
                    break;
                }
            }
            if (match) {
                return product;
            }
        }
        return undefined;
    }
    findProducts(typeRef, transformer, criteria) {
        let result = [];
        this.validateTypeRef(typeRef);
        const [groups, products] = this.splitProducts();
        if (criteria) {
            const validationResult = criteria.validate(typeRef, this.schema);
            if (validationResult.length > 0) {
                for (const error of validationResult) {
                    console.warn(`Invalid criterion: ${error}`);
                }
                return transformer.transform(result, products, groups);
            }
        }
        for (const product of Object.values(this.products)) {
            if (productTypeGuard(product)) {
                if (product.type.typeRef === typeRef && (!criteria || criteria.meets(product.model))) {
                    result.push(product);
                }
            }
        }
        return transformer.transform(result, products, groups);
    }
    availableProductPropertyValues(typeRef, propertyPath, criteria) {
        const result = new Set();
        const visitedOptionsId = new Set();
        const products = this.findProducts(typeRef, new FlatProductTransformer(), criteria);
        for (const product of products) {
            const propertyValue = new PropertyPathWalker(propertyPath, this.schema, product.model, typeRef).getPropertyValue();
            if (propertyValue !== undefined) {
                const { value } = propertyValue;
                if (value instanceof SourcedValue) {
                    if (Array.isArray(value.value)) {
                        throw new Error('Array options are not supported');
                    }
                    const optionId = value.value._id;
                    if (!visitedOptionsId.has(optionId)) {
                        result.add(value.value);
                        visitedOptionsId.add(optionId);
                    }
                }
                else {
                    if (Array.isArray(value)) {
                        for (const item of value) {
                            result.add(item);
                        }
                    }
                    else if (typeof value === 'object') {
                        throw new Error('Not a primitive type');
                    }
                    else {
                        result.add(value);
                    }
                }
            }
        }
        return [...result];
    }
    getOptionGroupByType(datasetName, options, typeName) {
        const groups = this.optionsRegistry.getOptionGroups().map(group => (Object.assign(Object.assign({}, group), { children: group.children.filter(o => o.type.typeRef === typeName) })))
            .filter(group => group.children.length > 0);
        return groups;
    }
    getOptionsByType(datasetName, options, productName, typeName) {
        return {};
    }
    getProductGroupsTree(datasetName, options) {
        return this.products.ROOT;
    }
    getProductGroup(productGroupName) {
        return this.products[productGroupName];
    }
    getProductSchema(datasetName, options, productName) {
        return this.schema;
    }
    getComposedProperties(objectTypeRef, components) {
        const result = [];
        components.forEach(component => {
            let type = this.schema.getType(component);
            if (!type) {
                const componentTypeDefinition = this.typesMap.get(component);
                if (!componentTypeDefinition) {
                    throw new Error(`Components composition error: Cannot find ${component} Type`);
                }
                this.registerType(componentTypeDefinition);
                type = this.schema.getType(component);
            }
            if (type instanceof ObjectType) {
                if (type.components.length > 0) {
                    throw new Error(`Wrong type definition for type: ${type.name}. Object cannot contain a component that uses other components`);
                }
                const incompatibleProperty = type.properties.find(componentProperty => result.some(property => isPropertiesIncompatible(property, componentProperty)));
                if (incompatibleProperty) {
                    throw new Error(`${objectTypeRef} has incompatible property ${incompatibleProperty.name} in ${component} component`);
                }
                type.properties.forEach(property => result.push(property));
            }
            else {
                throw new Error(`Component ${component} should be an ObjectType`);
            }
        });
        return result;
    }
    registerType(type) {
        if (this.schema.getType(type.name)) {
            return;
        }
        switch (type.kind) {
            case TypeKind.Enum:
                if (![SystemType.Number, SystemType.String, SystemType.MLString].includes(type.valueType)) {
                    throw new Error(`Wrong type definition for type: ${type.name}. 
          Enum type should be on of: '${SystemType.MLString}', '${SystemType.String}', '${SystemType.Number}'`);
                }
                const valueType = type.valueType === SystemType.Number ? 'number' : 'string';
                const invalidValue = type.values.find(v => typeof v !== valueType);
                if (invalidValue) {
                    throw new Error(`Wrong type definition for enum: ${type.name}. ${invalidValue} value has incorrect type`);
                }
                this.schema.add(new EnumType(type.name, type.valueType, type.values));
                break;
            case TypeKind.Object:
                if (!Array.isArray(type.properties)) {
                    throw new Error(`Wrong type definition for type:${type.name}`);
                }
                let components = [];
                if (type.components) {
                    if (!Array.isArray(type.components)) {
                        throw new Error(`Wrong type definition for type: ${type.name}. Components should be an array`);
                    }
                    components = type.components;
                }
                const composed = this.getComposedProperties(type.name, components);
                const externals = type.externals || [];
                const compatibilityTablesDescriptor = new CompatibilityTablesDescriptorParser(type).parse();
                this.schema.add(new ObjectType(type.name, overrideProperties(type.name, composed, type.properties.map((p) => new Property(p.name, p.type, this.mapOpts(type.name, p, this.schema)))), components, externals, compatibilityTablesDescriptor));
                break;
            case TypeKind.Union:
                const types = type.types;
                if (!Array.isArray(types)) {
                    throw new Error(`Types in Union ${type.name} should be an array`);
                }
                this.schema.add(new UnionType(type.name, types));
                break;
            case TypeKind.List:
                const nullable = type.nullable;
                if (nullable != null && typeof nullable !== 'boolean') {
                    throw new Error(`Wrong type definition for ${type.name}. Nullable should be boolean`);
                }
                this.schema.add(new ListType(type.name, type.elementType, nullable));
                break;
            case TypeKind.Primitive:
                this.schema.add(new PrimitiveType(type.name, type.elementType));
                break;
            default:
                break;
        }
    }
    validateOptions() {
        this.optionsRegistry.getOptions().forEach((option) => {
            try {
                const simpleVisitor = new SimpleVisitor(this.schema);
                const traverser = new PrototypeTraverser(this.schema, option.type.typeRef, option.model);
                traverser.map(simpleVisitor);
            }
            catch (e) {
                throw new Error(`Invalid option data "${option._id}". ${e.message}`);
            }
        });
    }
    validateProducts() {
    }
    mapOpts(typeRef, property, schema) {
        const opts = property.opts;
        if (!opts) {
            return {};
        }
        const invalidOpts = Object.keys(opts)
            .filter(o => !['needSource', 'metaOnly', 'constraints', 'availability', 'default', 'checker'].includes(o));
        if (invalidOpts.length) {
            throw new Error(`Wrong type definition of ${typeRef}. Invalid options: ${invalidOpts.join(',')}`);
        }
        let prOpts = opts;
        let availability = [];
        if (opts['availability']) {
            for (let av of opts['availability']) {
                let expr = createExpressionConstraint(av, schema);
                if (expr) {
                    availability.push(expr);
                }
            }
        }
        prOpts.availability = availability;
        if (prOpts.default != undefined) {
            const propertyType = this.schema.getType(property.type);
            if (propertyType) {
                switch (propertyType.kind) {
                    case TypeKind.Primitive:
                        prOpts.default = this.primitiveParser.parsePrimitive(propertyType.getTypeReference(), prOpts.default);
                        break;
                    case TypeKind.Union:
                        this.validateDefaultObject(prOpts.default, propertyType.getTypeReference());
                        break;
                }
            }
        }
        return prOpts;
    }
    validateDefaultObject(object, typeRef) {
        try {
            const simpleVisitor = new ValidationVisitor(this.schema);
            const traverser = new PrototypeTraverser(this.schema, typeRef, object);
            traverser.map(simpleVisitor);
        }
        catch (e) {
            throw new Error(`Invalid default object for type reference "${typeRef}". ${e.message}`);
        }
    }
    parseExternals(type) {
        const typeExternals = type.externals || [];
        typeExternals.forEach(external => {
            const paths = external.properties;
            paths.forEach(path => {
                const pathType = this.propertyPathValidator.parsePropertyType(path, type, external);
                if (!pathType) {
                    throw SchemaError.invalidExternalPropertyPath(path, `"${type.name}" of "${external.name}" external field.`);
                }
                const typeRef = pathType.getTypeReference();
                if (pathType.kind !== TypeKind.Primitive && pathType.kind !== TypeKind.Enum) {
                    throw SchemaError.invalidExternalPropertyPath(path, `"${type.name}" of "${external.name} external field. Property path should reference a primitive type but reference "${typeRef}" instead`);
                }
            });
        });
        type.externals = typeExternals.map(external => this.externalParser.parseDefinition(external, type.name));
    }
    getProductById(id) {
        const product = this.products[id];
        const compatibilityTables = this.compatibilityTables;
        return {
            product: castProduct(product),
            compatibilityTables
        };
    }
    validateTypeRef(typeRef) {
        if (this.schema.getType(typeRef) === undefined) {
            throw new Error(`Schema doesn't contain target type ${typeRef}`);
        }
    }
    parseResolutionSchema(loadedSchema) {
        if (loadedSchema && loadedSchema['products']) {
            const registeredTypes = new Set(this.typesMap.keys());
            const parser = new ResolutionSchemaParser(loadedSchema, registeredTypes);
            this.schema.resolutionSchemas = parser.parse();
        }
    }
    findOptionGroupById(id) {
        return this.optionsRegistry.findOptionGroupById(id);
    }
    getOptions(typeRef) {
        return this.optionsRegistry.getOptions()
            .filter(option => option.kind === RecordKind.Option && this.isTypeParentFor(typeRef, option.type.typeRef));
    }
    isTypeParentFor(parent, child) {
        if (parent === child) {
            return true;
        }
        const parentType = this.schema.getType(parent);
        if (parentType.kind !== TypeKind.Union) {
            return false;
        }
        const unionParentType = parentType;
        return unionParentType.types.some(childType => this.isTypeParentFor(childType, child));
    }
    findOption(id) {
        return this.optionsRegistry.getOptionById(id);
    }
}
//# sourceMappingURL=AsyncJsDatasetService.js.map